import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Sidebar, Section } from "@/pages/CustomerDashboard";
import {
  ChartBar, Users, CreditCard, CalendarCheck, Scan, Gear, BellRinging,
  Plus, PencilSimple, Trash, Warning, House, PlugsConnected, ArrowsClockwise,
  CheckCircle, XCircle, WifiHigh, UserGear, ShieldCheck, UserPlus, UserMinus,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export default function AdminDashboard() {
  const { user, logout, checkAuth } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  const [members, setMembers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [classes, setClasses] = useState([]);
  const [services, setServices] = useState([]);
  const [content, setContent] = useState({});
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    const [st, m, p, c, s, ct] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/members"), api.get("/plans"),
      api.get("/classes"), api.get("/services"), api.get("/content"),
    ]);
    setStats(st.data); setMembers(m.data); setPlans(p.data);
    setClasses(c.data); setServices(s.data); setContent(ct.data);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const nav = [
    { id: "overview", label: "Overview", icon: ChartBar },
    { id: "members", label: "Members", icon: Users },
    { id: "roles", label: "User Roles", icon: UserGear },
    { id: "plans", label: "Plans", icon: CreditCard },
    { id: "classes", label: "Classes", icon: CalendarCheck },
    { id: "services", label: "Services", icon: Scan },
    { id: "content", label: "Site Content", icon: Gear },
    { id: "integrations", label: "Integrations", icon: PlugsConnected },
  ];

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      <Sidebar nav={nav} tab={tab} setTab={setTab} logout={logout} navigate={navigate} name={user?.name} role="Owner / Admin" onAccountClick={() => setTab("profile")} />
      <main className="flex-1 md:ml-64 p-6 lg:p-10 max-w-6xl">
        {tab === "overview" && (
          <Section title="Dashboard" subtitle="Your gym at a glance.">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <Stat label="Members" value={stats.total_members ?? 0} icon={Users} />
              <Stat label="Active Subs" value={stats.active_subscriptions ?? 0} icon={CreditCard} accent />
              <Stat label="GymMaster Members" value={stats.gm_members ?? 0} icon={PlugsConnected} />
              <Stat label="Overdue" value={stats.overdue ?? 0} icon={Warning} warn={stats.overdue > 0} />
            </div>
            <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-6">
              <p className="text-sm text-zinc-500">Total bookings recorded: <span className="font-bold text-zinc-950">{stats.bookings ?? 0}</span></p>
            </div>
          </Section>
        )}

        {tab === "members" && <MembersTab members={members} refresh={refresh} />}
        {tab === "roles" && <RolesTab currentUserId={user?.id} />}
        {tab === "profile" && <AdminProfileTab user={user} checkAuth={checkAuth} />}
        {tab === "plans" && <CrudTab type="plans" title="Membership Plans" items={plans} refresh={refresh} fields={planFields} />}
        {tab === "classes" && <CrudTab type="classes" title="Class Schedule" items={classes} refresh={refresh} fields={classFields} />}
        {tab === "services" && <CrudTab type="services" title="Services" items={services} refresh={refresh} fields={serviceFields} />}
        {tab === "content" && <ContentTab content={content} refresh={refresh} />}
        {tab === "integrations" && <IntegrationsTab />}
      </main>
    </div>
  );
}

function MembersTab({ members, refresh }) {
  const [notify, setNotify] = useState(null);
  const [msg, setMsg] = useState("");

  const markOverdue = async (uid) => {
    try {
      await api.post(`/admin/members/${uid}/mark-overdue`);
      toast.success("Member marked overdue");
      refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendNotify = async () => {
    try {
      await api.post("/admin/notify", { user_id: notify.id, message: msg || undefined, type: "overdue" });
      toast.success(`Reminder sent to ${notify.name}`);
      setNotify(null); setMsg("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Section title="Members" subtitle="Manage members and their subscriptions.">
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="hidden sm:grid grid-cols-12 gap-2 px-5 py-3 bg-zinc-50 text-xs font-bold uppercase tracking-widest text-zinc-400">
          <span className="col-span-4">Member</span>
          <span className="col-span-3">Plan</span>
          <span className="col-span-2">Status</span>
          <span className="col-span-3 text-right">Actions</span>
        </div>
        {members.length === 0 && <p className="p-8 text-center text-zinc-400 font-bold">No members yet.</p>}
        {members.map((m) => (
          <div key={m.id} className="grid sm:grid-cols-12 gap-2 px-5 py-4 border-t border-zinc-100 items-center" data-testid={`member-${m.id}`}>
            <div className="sm:col-span-4">
              <p className="font-bold">{m.name}</p>
              <p className="text-sm text-zinc-500">{m.email}</p>
            </div>
            <div className="sm:col-span-3 text-sm">{m.subscription?.plan_name || "—"}</div>
            <div className="sm:col-span-2">
              <span className={`text-xs font-bold uppercase px-2.5 py-1 rounded-full ${m.subscription?.status === "active" ? "text-green-600 bg-green-50" : m.subscription?.status === "overdue" ? "text-red-600 bg-red-50" : "text-zinc-500 bg-zinc-100"}`}>
                {m.subscription?.status || "none"}
              </span>
            </div>
            <div className="sm:col-span-3 flex sm:justify-end gap-2">
              <button onClick={() => markOverdue(m.id)} data-testid={`mark-overdue-${m.id}`} className="text-xs font-bold border-2 border-zinc-200 rounded-sm px-3 py-1.5 hover:border-red-500 hover:text-red-600">Mark overdue</button>
              <button onClick={() => setNotify(m)} data-testid={`notify-${m.id}`} className="inline-flex items-center gap-1 text-xs font-bold bg-[#FF4F00] text-white rounded-sm px-3 py-1.5 hover:bg-[#E04600]">
                <BellRinging size={14} weight="bold" /> Notify
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!notify} onOpenChange={(o) => !o && setNotify(null)}>
        <DialogContent className="rounded-sm" data-testid="notify-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl font-black">Send reminder to {notify?.name}</DialogTitle></DialogHeader>
          <Label>Message (optional)</Label>
          <Textarea data-testid="notify-message" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Leave blank to send the default overdue-payment reminder…" className="rounded-sm" />
          <DialogFooter>
            <button onClick={sendNotify} data-testid="send-notify-btn" className="bg-[#FF4F00] text-white rounded-sm px-6 py-2.5 font-bold hover:bg-[#E04600]">Send reminder</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}

function RolesTab({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRole = async (u, role) => {
    setBusy(u.id);
    try {
      await api.put(`/admin/users/${u.id}/role`, { role });
      toast.success(`${u.name} is now ${role === "admin" ? "an admin" : "a member"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email})? This permanently deletes their account, bookings and alerts.`)) return;
    setBusy(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.name} has been removed`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const admins = users.filter((u) => u.role === "admin").length;

  return (
    <Section title="User Roles" subtitle="Promote members to admin or revoke admin access.">
      <div className="mb-5 flex gap-4">
        <div className="bg-white border border-zinc-200 rounded-sm px-5 py-3">
          <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Total users</p>
          <p className="font-heading text-2xl font-black">{users.length}</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm px-5 py-3">
          <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Admins</p>
          <p className="font-heading text-2xl font-black">{admins}</p>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="hidden sm:grid grid-cols-12 gap-2 px-5 py-3 bg-zinc-50 text-xs font-bold uppercase tracking-widest text-zinc-400">
          <span className="col-span-5">User</span>
          <span className="col-span-3">Role</span>
          <span className="col-span-4 text-right">Action</span>
        </div>
        {users.length === 0 && <p className="p-8 text-center text-zinc-400 font-bold">No users yet.</p>}
        {users.map((u) => {
          const isAdmin = u.role === "admin";
          const isSelf = u.id === currentUserId;
          return (
            <div key={u.id} className="grid sm:grid-cols-12 gap-2 px-5 py-4 border-t border-zinc-100 items-center" data-testid={`role-row-${u.id}`}>
              <div className="sm:col-span-5">
                <p className="font-bold">{u.name} {isSelf && <span className="text-xs text-zinc-400">(you)</span>}</p>
                <p className="text-sm text-zinc-500">{u.email}</p>
              </div>
              <div className="sm:col-span-3">
                <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase px-2.5 py-1 rounded-full ${isAdmin ? "text-[#FF4F00] bg-orange-50" : "text-zinc-600 bg-zinc-100"}`} data-testid={`role-badge-${u.id}`}>
                  {isAdmin ? <ShieldCheck size={13} weight="fill" /> : <Users size={13} weight="bold" />} {u.role}
                </span>
              </div>
              <div className="sm:col-span-4 flex sm:justify-end items-center gap-2">
                {isSelf ? (
                  <span className="text-xs text-zinc-400 font-bold">Can't change own account</span>
                ) : (
                  <>
                    {isAdmin ? (
                      <button onClick={() => setRole(u, "customer")} disabled={busy === u.id} data-testid={`demote-${u.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-bold border-2 border-zinc-200 rounded-sm px-3 py-1.5 hover:border-red-500 hover:text-red-600 disabled:opacity-50">
                        <UserMinus size={14} weight="bold" /> Revoke admin
                      </button>
                    ) : (
                      <button onClick={() => setRole(u, "admin")} disabled={busy === u.id} data-testid={`promote-${u.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-bold bg-zinc-950 text-white rounded-sm px-3 py-1.5 hover:bg-zinc-800 disabled:opacity-50">
                        <UserPlus size={14} weight="bold" /> Make admin
                      </button>
                    )}
                    <button onClick={() => removeUser(u)} disabled={busy === u.id} data-testid={`remove-${u.id}`} title="Remove user"
                      className="inline-flex items-center justify-center w-8 h-8 rounded-sm border-2 border-zinc-200 hover:border-red-500 hover:text-red-600 disabled:opacity-50">
                      <Trash size={15} weight="bold" />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}


const planFields = [
  { name: "name", label: "Name" },
  { name: "price", label: "Price", type: "number" },
  { name: "interval", label: "Interval (week/fortnight/1 month/day...)" },
  { name: "category", label: "Category (ongoing/prepaid/pass)" },
  { name: "description", label: "Description" },
  { name: "features", label: "Features (comma separated)", list: true },
  { name: "recommended", label: "Recommended", bool: true },
];
const classFields = [
  { name: "title", label: "Title" },
  { name: "type", label: "Type" },
  { name: "coach", label: "Coach" },
  { name: "day", label: "Day" },
  { name: "time", label: "Time" },
  { name: "capacity", label: "Capacity", type: "number" },
];
const serviceFields = [
  { name: "name", label: "Name" },
  { name: "type", label: "Type" },
  { name: "price", label: "Price", type: "number" },
  { name: "duration", label: "Duration" },
  { name: "description", label: "Description" },
];

function blankFrom(fields) {
  const o = {};
  fields.forEach((f) => { o[f.name] = f.list ? "" : f.bool ? false : f.type === "number" ? 0 : ""; });
  return o;
}

function CrudTab({ type, title, items, refresh, fields }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const open = (item) => {
    if (item) {
      const f = { ...item };
      fields.forEach((fl) => { if (fl.list && Array.isArray(f[fl.name])) f[fl.name] = f[fl.name].join(", "); });
      setForm(f);
    } else {
      setForm(blankFrom(fields));
    }
    setEditing(item || {});
  };

  const save = async () => {
    const payload = { ...form };
    fields.forEach((f) => {
      if (f.list) payload[f.name] = String(payload[f.name] || "").split(",").map((x) => x.trim()).filter(Boolean);
      if (f.type === "number") payload[f.name] = parseFloat(payload[f.name]) || 0;
      if (f.bool) payload[f.name] = !!payload[f.name];
    });
    delete payload.id; delete payload.booked_count;
    try {
      if (editing.id) await api.put(`/admin/${type}/${editing.id}`, payload);
      else await api.post(`/admin/${type}`, payload);
      toast.success("Saved");
      setEditing(null); refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/${type}/${id}`); toast.success("Deleted"); refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Section title={title} subtitle="Create, edit and remove.">
      <button onClick={() => open(null)} data-testid={`add-${type}`} className="mb-5 inline-flex items-center gap-2 bg-zinc-950 text-white rounded-sm px-5 py-2.5 font-bold hover:bg-zinc-800">
        <Plus size={18} weight="bold" /> Add new
      </button>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((it) => (
          <div key={it.id} className="bg-white border border-zinc-200 rounded-sm p-5 flex items-start justify-between" data-testid={`${type}-item-${it.id}`}>
            <div>
              <p className="font-bold">{it.name || it.title}</p>
              <p className="text-sm text-zinc-500">
                {type === "classes" ? `${it.day} · ${it.time} · ${it.coach}` : `$${it.price}${it.interval ? "/" + it.interval : ""}`}
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => open(it)} data-testid={`edit-${it.id}`} className="w-9 h-9 rounded-sm border-2 border-zinc-200 flex items-center justify-center hover:border-zinc-950"><PencilSimple size={16} weight="bold" /></button>
              <button onClick={() => del(it.id)} data-testid={`delete-${it.id}`} className="w-9 h-9 rounded-sm border-2 border-zinc-200 flex items-center justify-center hover:border-red-500 hover:text-red-600"><Trash size={16} weight="bold" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-sm max-h-[85vh] overflow-y-auto" data-testid={`${type}-dialog`}>
          <DialogHeader><DialogTitle className="font-heading text-2xl font-black">{editing?.id ? "Edit" : "New"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {fields.map((f) => (
              <div key={f.name} className="space-y-1.5">
                <Label>{f.label}</Label>
                {f.bool ? (
                  <input type="checkbox" data-testid={`field-${f.name}`} checked={!!form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.checked })} className="w-5 h-5 accent-[#FF4F00]" />
                ) : (
                  <Input type={f.type || "text"} data-testid={`field-${f.name}`} value={form[f.name] ?? ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className="rounded-sm" />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <button onClick={save} data-testid={`save-${type}`} className="bg-[#FF4F00] text-white rounded-sm px-6 py-2.5 font-bold hover:bg-[#E04600]">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}

function ContentTab({ content, refresh }) {
  const [form, setForm] = useState(content);
  useEffect(() => { setForm(content); }, [content]);
  const fields = [
    { name: "hero_title", label: "Hero title" },
    { name: "hero_subtitle", label: "Hero subtitle" },
    { name: "hours", label: "Opening hours" },
    { name: "address", label: "Address" },
    { name: "email", label: "Email" },
    { name: "phone", label: "Phone" },
    { name: "about", label: "About", area: true },
  ];
  const save = async () => {
    try { await api.put("/admin/content", form); toast.success("Content updated"); refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Section title="Site Content" subtitle="Edit what visitors see on the landing page.">
      <div className="bg-white border border-zinc-200 rounded-sm p-6 max-w-2xl space-y-4">
        {fields.map((f) => (
          <div key={f.name} className="space-y-1.5">
            <Label>{f.label}</Label>
            {f.area ? (
              <Textarea data-testid={`content-${f.name}`} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className="rounded-sm" />
            ) : (
              <Input data-testid={`content-${f.name}`} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className="rounded-sm" />
            )}
          </div>
        ))}
        <button onClick={save} data-testid="save-content" className="bg-zinc-950 text-white rounded-sm px-6 py-3 font-bold hover:bg-zinc-800">Save changes</button>
      </div>
    </Section>
  );
}

function AdminProfileTab({ user, checkAuth }) {
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/profile", form);
      await checkAuth();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section title="My Profile" subtitle="Manage your account details.">
      <div className="bg-white border border-zinc-200 rounded-sm p-6 max-w-lg space-y-4">
        <div className="flex items-center gap-4 pb-4 border-b border-zinc-100">
          <div className="w-14 h-14 rounded-full bg-[#FF4F00] text-white flex items-center justify-center font-black text-xl">
            {(user?.name || "?").trim().charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-bold">{user?.name}</p>
            <p className="text-sm text-zinc-500">{user?.email}</p>
            <span className="inline-flex items-center gap-1 text-xs font-bold uppercase text-[#FF4F00] bg-orange-50 px-2 py-0.5 rounded-full mt-1">
              <ShieldCheck size={12} weight="fill" /> {user?.role}
            </span>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Name</Label>
          <Input data-testid="admin-profile-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" />
        </div>
        <div className="space-y-1.5">
          <Label>Email</Label>
          <Input value={user?.email || ""} disabled className="rounded-sm bg-zinc-50" />
        </div>
        <div className="space-y-1.5">
          <Label>Phone</Label>
          <Input data-testid="admin-profile-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm" />
        </div>
        <button onClick={save} disabled={busy} data-testid="admin-profile-save" className="bg-zinc-950 text-white rounded-sm px-6 py-3 font-bold hover:bg-zinc-800 disabled:opacity-50">
          {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </Section>
  );
}


function IntegrationsTab() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/integrations/gymmaster");
      setStatus(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (action, url) => {
    setBusy(action);
    try {
      const { data } = await api.post(url);
      toast.success(data.message || `Done — ${data.count ?? ""} records`.trim());
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const configured = status?.configured;
  const fmt = (t) => (t ? new Date(t).toLocaleString() : "Never");

  return (
    <Section title="Integrations" subtitle="Connect your gym's existing tools.">
      <div className="bg-white border border-zinc-200 rounded-sm p-6 max-w-3xl" data-testid="gymmaster-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-sm bg-zinc-950 flex items-center justify-center text-white"><PlugsConnected size={24} weight="bold" /></div>
            <div>
              <h3 className="font-heading text-xl font-black">GymMaster</h3>
              <p className="text-sm text-zinc-500">Sync members & class timetable from your GymMaster account.</p>
              <p className="text-xs text-zinc-400 mt-0.5" data-testid="gymmaster-auto-sync">
                Auto-syncs hourly · last auto-sync: {status?.last_auto_sync ? new Date(status.last_auto_sync).toLocaleString() : "pending…"}
              </p>
            </div>
          </div>
          <span
            data-testid="gymmaster-status-badge"
            className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full ${configured ? "text-green-600 bg-green-50" : "text-amber-600 bg-amber-50"}`}
          >
            {configured ? <CheckCircle size={14} weight="fill" /> : <Warning size={14} weight="fill" />}
            {configured ? "Connected" : "Not configured"}
          </span>
        </div>

        {!configured && (
          <div className="mt-5 bg-zinc-50 border border-zinc-200 rounded-sm p-4 text-sm text-zinc-600" data-testid="gymmaster-setup-note">
            <p className="font-bold text-zinc-950 mb-1">To connect:</p>
            <ol className="list-decimal ml-5 space-y-1">
              <li>In GymMaster go to <span className="font-semibold">Settings → Integrations → Member Portal API</span>.</li>
              <li>Copy your <span className="font-semibold">Club Name</span> (site subdomain) and <span className="font-semibold">API Key</span>.</li>
              <li>Add them to the backend as <code className="bg-zinc-200 px-1 rounded">GYMMASTER_SITE_NAME</code> and <code className="bg-zinc-200 px-1 rounded">GYMMASTER_API_KEY</code>, then restart.</li>
            </ol>
          </div>
        )}

        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="border border-zinc-200 rounded-sm p-4">
            <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Site</p>
            <p className="font-bold mt-1 truncate">{status?.site_name || "—"}</p>
          </div>
          <div className="border border-zinc-200 rounded-sm p-4">
            <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Members synced</p>
            <p className="font-heading text-2xl font-black mt-1">{status?.members_synced ?? 0}</p>
            <p className="text-xs text-zinc-400">{fmt(status?.last_members_sync)}</p>
          </div>
          <div className="border border-zinc-200 rounded-sm p-4">
            <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Plans synced</p>
            <p className="font-heading text-2xl font-black mt-1">{status?.plans_synced ?? 0}</p>
            <p className="text-xs text-zinc-400">{fmt(status?.last_plans_sync)}</p>
          </div>
          <div className="border border-zinc-200 rounded-sm p-4">
            <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Classes synced</p>
            <p className="font-heading text-2xl font-black mt-1">{status?.classes_synced ?? 0}</p>
            <p className="text-xs text-zinc-400">{fmt(status?.last_classes_sync)}</p>
          </div>
        </div>

        {status?.last_error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-sm p-3 text-sm text-red-600 flex items-start gap-2" data-testid="gymmaster-error">
            <XCircle size={18} weight="bold" className="mt-0.5 shrink-0" /> <span>{status.last_error}</span>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={() => run("test", "/admin/integrations/gymmaster/test")} disabled={busy} data-testid="gymmaster-test-btn"
            className="inline-flex items-center gap-2 border-2 border-zinc-200 rounded-sm px-4 py-2.5 font-bold text-sm hover:border-zinc-950 disabled:opacity-50">
            <WifiHigh size={18} weight="bold" /> {busy === "test" ? "Testing…" : "Test connection"}
          </button>
          <button onClick={() => run("members", "/admin/integrations/gymmaster/sync/members")} disabled={busy} data-testid="gymmaster-sync-members-btn"
            className="inline-flex items-center gap-2 bg-zinc-950 text-white rounded-sm px-4 py-2.5 font-bold text-sm hover:bg-zinc-800 disabled:opacity-50">
            <ArrowsClockwise size={18} weight="bold" /> {busy === "members" ? "Syncing…" : "Sync members"}
          </button>
          <button onClick={() => run("plans", "/admin/integrations/gymmaster/sync/plans")} disabled={busy} data-testid="gymmaster-sync-plans-btn"
            className="inline-flex items-center gap-2 bg-zinc-950 text-white rounded-sm px-4 py-2.5 font-bold text-sm hover:bg-zinc-800 disabled:opacity-50">
            <ArrowsClockwise size={18} weight="bold" /> {busy === "plans" ? "Syncing…" : "Sync plans"}
          </button>
          <button onClick={() => run("classes", "/admin/integrations/gymmaster/sync/classes")} disabled={busy} data-testid="gymmaster-sync-classes-btn"
            className="inline-flex items-center gap-2 bg-[#FF4F00] text-white rounded-sm px-4 py-2.5 font-bold text-sm hover:bg-[#E04600] disabled:opacity-50">
            <ArrowsClockwise size={18} weight="bold" /> {busy === "classes" ? "Syncing…" : "Sync classes"}
          </button>
        </div>
      </div>
    </Section>
  );
}

const Stat = ({ label, value, icon: Icon, accent, warn }) => (
  <div className={`rounded-sm p-5 border ${warn ? "bg-red-50 border-red-200" : accent ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-200"}`}>
    <div className="flex items-center justify-between">
      <p className={`text-xs uppercase tracking-widest font-bold ${accent ? "text-zinc-400" : "text-zinc-400"}`}>{label}</p>
      <Icon size={20} weight="bold" className={warn ? "text-red-500" : accent ? "text-[#FF4F00]" : "text-zinc-300"} />
    </div>
    <p className="font-heading text-3xl font-black mt-2">{value}</p>
  </div>
);
